/**
 * The facts review surface — the trust surface (BRIEF, product scope 2).
 *
 * What this screen is for: before or after a check run, a planner looks at
 * every value the Baueingabe-Check is about to reason with, sees where each one came from,
 * corrects the ones that are wrong, and watches the deterministic checks re-run
 * on the spot. That loop — correct → re-check → see exactly which findings
 * moved — is the product moment here, and it is why the whole screen is built
 * around one state machine rather than around a form.
 *
 * The loop, in order:
 *   1. `PATCH …/facts`   — the correction. The server sets that field's
 *      provenance to `manuell` and writes old → new into the audit trail.
 *   2. `POST …/recheck`  — synchronous, deterministic, measured at 38–124 ms
 *      server-side. It cannot call a model: the dossier handed to the pipeline
 *      carries no source directory. The answer already contains run, report and
 *      diff, so the change is on screen after one round trip.
 *   3. `GET …/facts`     — re-read, because the checklist and the notices are
 *      derived from the latest run and the correction just produced a new one.
 *
 * The brief's bar for step 2 is five seconds; the backend does it in about a
 * tenth of one. So there is no spinner, no progress bar and no full-page
 * reload — just a polite live region that says "wird neu geprüft" and is
 * replaced by the result, usually before anyone reads it.
 */
import { useCallback, useMemo, useState } from 'react'
import { ArrowLeft, FileText, RefreshCw } from 'lucide-react'

import { api } from '@/api/client'
import { CostNote } from '@/components/CostNote'
import { ErrorNote } from '@/components/ErrorNote'
import { Icon } from '@/components/Icon'
import { LoadingBlock } from '@/components/LoadingBlock'
import { NoticeList } from '@/components/NoticeList'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { FactRow } from '@/features/facts/FactRow'
import { MissingInputsChecklist } from '@/features/facts/MissingInputs'
import { RunDiffView } from '@/features/facts/RunDiffView'
import { useI18n, type TranslationKey } from '@/i18n'
import { bautypLabel, verfahrenLabel } from '@/i18n/enumLabels'
import { Link, buildPath, navigate } from '@/lib/router'
import { useAsync } from '@/lib/useAsync'
import type {
  DossierFileRef,
  FactField,
  FactGroup,
  FactValue,
  FactsView,
  RecheckResult,
  WorkspaceRef,
} from '@/api/types'

/** Display order. Stammdaten first because everything else depends on the zone. */
const GROUP_ORDER: FactGroup[] = [
  'stammdaten',
  'values',
  'form',
  'flags',
  'gis',
  'dokumente',
  'nachweise',
]

const GROUP_LABEL: Record<FactGroup, TranslationKey> = {
  stammdaten: 'facts.group.stammdaten',
  values: 'facts.group.values',
  form: 'facts.group.form',
  flags: 'facts.group.flags',
  gis: 'facts.group.gis',
  dokumente: 'facts.group.dokumente',
  nachweise: 'facts.group.nachweise',
}

type LoopState = 'idle' | 'saving' | 'rechecking'

export function FactsScreen({
  workspace,
  dossierId,
}: {
  workspace: WorkspaceRef
  dossierId: string
}) {
  const { t } = useI18n()
  const loaded = useAsync<FactsView>(
    (signal) => api.facts(workspace.id, dossierId, signal),
    [workspace.id, dossierId],
  )
  const files = useAsync<DossierFileRef[]>(
    (signal) => api.dossierFiles(workspace.id, dossierId, signal),
    [workspace.id, dossierId],
  )

  // `view` shadows the loaded data once a correction has been made — the PATCH
  // answer is the newest truth and re-fetching to get it would be a round trip
  // for nothing.
  const [view, setView] = useState<FactsView | null>(null)
  const [state, setState] = useState<LoopState>('idle')
  const [busyPath, setBusyPath] = useState<string | null>(null)
  const [result, setResult] = useState<RecheckResult | null>(null)
  const [error, setError] = useState<string | null>(null)

  const facts = view ?? loaded.data
  const busy = state !== 'idle'

  const runLoop = useCallback(
    async (edit: { path: string; value: FactValue } | null) => {
      setError(null)
      setResult(null)
      try {
        if (edit) {
          setState('saving')
          setBusyPath(edit.path)
          setView(await api.editFacts(workspace.id, dossierId, [edit]))
        }
        setState('rechecking')
        const recheck = await api.recheck(workspace.id, dossierId)
        setResult(recheck)
        // The checklist and the notices are derived from the newest run.
        setView(await api.facts(workspace.id, dossierId))
        // Shown change, not implied change: a correction made near the bottom
        // of a long table would otherwise land off-screen. Scroll, don't jump —
        // and `prefers-reduced-motion` turns the smooth scroll off (index.css).
        requestAnimationFrame(() => {
          const panel = document.querySelector('[data-testid="recheck-result"]')
          // jsdom has no layout, so `scrollIntoView` may not exist there.
          panel?.scrollIntoView?.({ behavior: 'smooth', block: 'nearest' })
        })
      } catch (cause) {
        // The server's own sentence — it says which value it refused and why.
        setError(cause instanceof Error ? cause.message : String(cause))
      } finally {
        setState('idle')
        setBusyPath(null)
      }
    },
    [workspace.id, dossierId],
  )

  const save = useCallback(
    (path: string, value: FactValue) => runLoop({ path, value }),
    [runLoop],
  )

  const jumpTo = useCallback((path: string) => {
    const row = document.querySelector<HTMLElement>(`[data-path="${CSS.escape(path)}"]`)
    row?.scrollIntoView?.({ behavior: 'smooth', block: 'center' })
    row?.querySelector<HTMLButtonElement>('button')?.focus()
  }, [])

  const fields = useMemo(() => facts?.fields ?? [], [facts])
  const knownPaths = useMemo(() => new Set(fields.map((field) => field.path)), [fields])
  const grouped = useMemo(() => {
    const map = new Map<FactGroup, FactField[]>()
    for (const field of fields) {
      const list = map.get(field.group) ?? []
      list.push(field)
      map.set(field.group, list)
    }
    return map
  }, [fields])

  if (loaded.loading && !facts) return <LoadingBlock />
  if (loaded.error && !facts)
    return (
      <ErrorNote message={loaded.error} onRetry={loaded.reload} retryLabel={t('common.retry')} />
    )
  if (!facts) return null

  const missing = facts.missing_inputs ?? []
  const lastRunId = result?.run.run_id ?? facts.last_run_id ?? null

  return (
    <div className="flex flex-col gap-10">
      <header className="flex flex-col gap-3">
        <Link
          to={buildPath('workspace', { workspaceId: workspace.id })}
          className="inline-flex items-center gap-2 self-start text-[13px] text-ink-2 underline underline-offset-2"
        >
          <Icon icon={ArrowLeft} />
          {workspace.name}
        </Link>
        <h1 className="font-display text-[32px] leading-tight font-semibold">
          {t('facts.title')}
        </h1>
        <p className="max-w-[75ch] text-[15px] text-muted">{t('facts.lead')}</p>
        <dl className="flex flex-wrap gap-x-8 gap-y-2 text-[13px]">
          <Meta label={t('report.gemeinde')} value={facts.gemeinde} />
          <Meta label={t('report.zone')} value={facts.zone} />
          <Meta label={t('upload.bautyp')} value={bautypLabel(facts.bautyp, t)} />
          <Meta label={t('upload.verfahren')} value={verfahrenLabel(facts.verfahren, t)} />
          {facts.case_id ? <Meta label={t('report.case')} value={facts.case_id} /> : null}
        </dl>
        {lastRunId ? (
          <div className="flex flex-wrap gap-3">
            <Button
              variant="secondary"
              size="sm"
              onClick={() => navigate(buildPath('report', { runId: lastRunId }))}
            >
              <Icon icon={FileText} />
              {t('facts.openReport')}
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => void runLoop(null)}
              disabled={busy || !facts.has_facts}
            >
              <Icon icon={RefreshCw} />
              {t('facts.recheckNow')}
            </Button>
          </div>
        ) : null}
      </header>

      {/* The extractor's own word on the model step — shown before any value. */}
      {facts.llm_status ? (
        <p
          data-testid="llm-status"
          className="rounded-sm border border-border bg-surface px-4 py-3 text-[13px] text-muted"
        >
          {t('facts.llmStatus')}: {facts.llm_status}
        </p>
      ) : null}

      <NoticeList notices={facts.notices} title={t('notices.runTitle')} />

      {error ? <ErrorNote message={error} /> : null}

      {/* ---- the correction loop's answer ---------------------------------- */}
      <RecheckPanel state={state} result={result} />

      {!facts.has_facts ? (
        <p
          data-testid="facts-empty"
          className="rounded-sm border border-border bg-surface px-4 py-3 text-[13px] text-muted"
        >
          {t('facts.noFactsYet')}
        </p>
      ) : null}

      {/* ---- fehlende Eingaben --------------------------------------------- */}
      {facts.has_facts ? (
        <section className="flex flex-col gap-4">
          <div>
            <h2 className="font-display text-[21px] font-semibold">{t('facts.missingTitle')}</h2>
            <p className="mt-1 max-w-[80ch] text-[13px] text-muted">{t('facts.missingLead')}</p>
          </div>
          <MissingInputsChecklist
            items={missing}
            knownPaths={knownPaths}
            busy={busy}
            onJump={jumpTo}
            onSave={save}
          />
        </section>
      ) : null}

      {/* ---- every fact, grouped ------------------------------------------- */}
      {GROUP_ORDER.filter((group) => (grouped.get(group)?.length ?? 0) > 0).map((group) => (
        <section key={group} className="flex flex-col gap-4">
          <h2 className="font-display text-[21px] font-semibold">{t(GROUP_LABEL[group])}</h2>
          <Card>
            <ul data-testid={`fact-group-${group}`} className="flex flex-col">
              {(grouped.get(group) ?? []).map((field) => (
                <FactRow
                  key={field.path}
                  field={field}
                  busy={busy && busyPath !== null && busyPath !== field.path}
                  onSave={save}
                />
              ))}
            </ul>
          </Card>
        </section>
      ))}

      {/* ---- what was read, and how ---------------------------------------- */}
      {(files.data?.length ?? 0) > 0 ? (
        <section className="flex flex-col gap-4">
          <h2 className="font-display text-[21px] font-semibold">{t('facts.filesTitle')}</h2>
          <Card>
            <ul className="flex flex-col">
              {(files.data ?? []).map((file) => (
                <li
                  key={file.id}
                  data-testid="dossier-file"
                  className="flex flex-wrap items-center gap-3 border-b border-border px-4 py-3 text-[13px] last:border-b-0"
                >
                  <Icon icon={FileText} className="text-muted" />
                  <span className="text-ink">{file.filename}</span>
                  <span className="text-muted">{t(`facts.kind.${file.content_kind}`)}</span>
                  {file.pages ? (
                    <span className="num text-muted">
                      {file.pages} {t('facts.pages')}
                    </span>
                  ) : null}
                  {(file.classified_as ?? []).length > 0 ? (
                    <span className="text-muted">{(file.classified_as ?? []).join(', ')}</span>
                  ) : null}
                </li>
              ))}
            </ul>
          </Card>
        </section>
      ) : null}

      {/* Founder-only, and only when the server actually sent a cost object. */}
      <CostNote cost={result?.run.cost} />
    </div>
  )
}

/**
 * The loop's live region.
 *
 * `aria-live="polite"` rather than an animation: the re-check lands in about a
 * tenth of a second, and a spinner that appears and vanishes inside 100 ms is
 * theatre. What a reader needs afterwards is the measurement (how long it
 * took, that no model was involved) and the diff.
 */
function RecheckPanel({ state, result }: { state: LoopState; result: RecheckResult | null }) {
  const { t } = useI18n()

  return (
    <div aria-live="polite" className="flex flex-col gap-3">
      {state !== 'idle' ? (
        <p
          data-testid="recheck-status"
          className="rounded-sm border border-border bg-surface px-4 py-3 text-[13px] text-ink-2"
        >
          {state === 'saving' ? t('facts.saving') : t('facts.rechecking')}
        </p>
      ) : null}

      {result ? (
        <Card data-testid="recheck-result" className="motion-safe:animate-fade">
          <CardHeader>
            <CardTitle>{t('facts.recheckTitle')}</CardTitle>
            <p className="text-[13px] text-muted">
              {t('facts.recheckMeta', { ms: Math.round(result.duration_ms) })}
            </p>
          </CardHeader>
          <CardContent className="flex flex-col gap-5">
            <RunDiffView diff={result.diff} />
            {/* A distinct label: two landmarks with the same accessible name
                on one page is an a11y defect (axe: landmark-unique). */}
            <NoticeList notices={result.run.notices} title={t('notices.recheckTitle')} testId="recheck-notices" />
          </CardContent>
        </Card>
      ) : null}
    </div>
  )
}

function Meta({ label, value }: { label: string; value?: string | null }) {
  const { t } = useI18n()
  return (
    <div className="flex gap-2">
      <dt className="text-muted">{label}</dt>
      <dd className="num text-ink-2">{value ?? t('facts.empty')}</dd>
    </div>
  )
}
